<template>
  <div class="product-view">
    <MainNavbar />

    <nav class="store-breadcrumb" aria-label="Breadcrumb">
      <ol class="store-breadcrumb__trail">
        <li class="store-breadcrumb__item">
          <router-link :to="{ name: 'MainPage' }" class="store-breadcrumb__link">
            <span class="store-breadcrumb__icon" aria-hidden="true">
              <i class="fa-solid fa-store"></i>
            </span>
            <span>Store</span>
          </router-link>
        </li>
        <li class="store-breadcrumb__sep" aria-hidden="true">
          <i class="fa-solid fa-chevron-right"></i>
        </li>
        <li class="store-breadcrumb__item store-breadcrumb__item--current">
          <span class="store-breadcrumb__current" aria-current="page">{{
            product?.name || "Product"
          }}</span>
        </li>
      </ol>
    </nav>

    <!-- Product Content -->
    <div v-if="product" class="product-container">
      <div class="product-layout">
        <!-- LEFT: IMAGE GALLERY -->
        <div class="image-section">
          <!-- Main Image Container -->
          <div class="main-image-wrapper">
            <div class="main-image-container">
              <transition name="snapshot">
                <img
                  :key="activeImage"
                  :src="activeImage"
                  class="main-image"
                  :alt="product.name"
                  decoding="async"
                  @error="handleImageError"
                />
              </transition>
            </div>

            <!-- Discount Badge -->
            <div v-if="product.discounted" class="discount-badge">
              <span class="discount-percent">
                {{ Math.round(product.discount_price) }}% OFF
              </span>
            </div>

            <!-- Image Navigation (non-footwear only) -->
            <div
              v-if="!hasColorVariants && product.images && product.images.length > 1"
              class="image-navigation"
            >
              <button
                class="nav-btn nav-prev"
                @click="previousImage"
                :disabled="isFirstImage"
                aria-label="Previous image"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>

              <div class="image-counter">
                {{ currentImageIndex + 1 }} / {{ product.images.length }}
              </div>

              <button
                class="nav-btn nav-next"
                @click="nextImage"
                :disabled="isLastImage"
                aria-label="Next image"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <!-- ── Color Variant Swatches (footwear and apparel) ── -->
          <div v-if="hasColorVariants" class="color-variants-section">
            <p class="color-variants-label">
              Color
              <span
                v-if="displayVariant"
                class="color-variants-name"
              >— {{ displayVariant.label }}</span>
            </p>

            <div class="color-swatches-row">
              <button
                v-for="variant in colorVariants"
                :key="variant.id"
                :class="[
                  'color-swatch',
                  {
                    'color-swatch--committed': variant.id === committedColorId,
                    'color-swatch--previewing': variant.id === previewColorId,
                  }
                ]"
                :aria-label="`${variant.label} colorway`"
                :aria-pressed="variant.id === committedColorId"
                @click="onSwatchClick(variant.id)"
                @mouseenter="onSwatchHover(variant.id)"
                @mouseleave="onSwatchLeave"
              >
                <span
                  v-if="!variant.secondary"
                  class="swatch-solid"
                  :style="{ background: swatchColor(variant.primary) }"
                ></span>
                <template v-else>
                  <!-- Left half → primary color -->
                  <span
                    class="swatch-half swatch-half--left"
                    :style="{ background: swatchColor(variant.primary) }"
                  ></span>
                  <!-- Right half → secondary color -->
                  <span
                    class="swatch-half swatch-half--right"
                    :style="{ background: swatchColor(variant.secondary) }"
                  ></span>
                </template>
              </button>
            </div>
          </div>

          <!-- ── Variant View Strip (footwear views, or apparel undressed/dressed) ── -->
          <div
            v-if="hasColorVariants && activeVariantViews.length"
            class="variant-view-strip"
          >
            <button
              v-for="viewItem in activeVariantViews"
              :key="viewItem.key"
              :class="[
                'variant-view-btn',
                { 'variant-view-btn--active': activeImage === viewItem.url }
              ]"
              :aria-label="`${viewItem.key} view`"
              @click="onVariantViewClick(viewItem.url)"
            >
              <img
                :src="viewItem.url"
                class="variant-view-thumb"
                :alt="`${product.name} ${viewItem.key} view`"
                loading="lazy"
                decoding="async"
                @error="handleImageError"
              />
              <span class="variant-view-label">{{ viewItem.key }}</span>
            </button>
          </div>

          <!-- Thumbnail Gallery (non-footwear products) -->
          <div
            v-if="!hasColorVariants && product.images && product.images.length > 1"
            class="thumbnail-gallery"
          >
            <div class="thumbnail-scroll">
              <button
                v-for="(img, i) in product.images"
                :key="i"
                :class="['thumbnail-btn', { active: img === activeImage }]"
                @click="switchToImage(img, i)"
                :aria-label="`View image ${i + 1}`"
                :aria-current="img === activeImage ? 'true' : 'false'"
              >
                <img
                  :src="img"
                  class="thumbnail"
                  :alt="`${product.name} view ${i + 1}`"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- RIGHT: PRODUCT DETAILS -->
        <div class="details-section">
          <!-- Product Header -->
          <div class="product-header">
            <div class="product-badges">
              <span v-if="product.discounted" class="badge badge-discount">
                <i class="fa-solid fa-bolt"></i> Sale
              </span>

              <span
                v-if="product.stock_quantity > 0 && product.stock_quantity <= 5"
                class="badge badge-warning"
              >
                <i class="fa-solid fa-fire"></i> Almost Gone
              </span>

              <span class="badge badge-shipping">
                <i class="fa-solid fa-rocket"></i> Fast Shipping
              </span>
            </div>

            <h1 class="product-name">{{ product.name }}</h1>

            <div class="product-meta">
              <span class="brand">
                <i class="fa-solid fa-copyright"></i>
                {{ product.brand }}
              </span>

              <span class="category">
                <i class="fa-solid fa-tag"></i>
                {{ product.category }}
              </span>
            </div>

            <!-- Rating -->
            <div class="rating-section">
              <div class="stars">
                <i
                  v-for="n in 5"
                  :key="n"
                  class="fa-star"
                  :class="getStarClass(averageRating, n)"
                ></i>
              </div>

              <span class="rating-text">
                {{ averageRating }} •
                {{ reviewCount }} {{ reviewCount === 1 ? "review" : "reviews" }}
              </span>

              <a href="#reviews" class="review-link" @click.prevent="scrollToReviews">
                See all reviews
              </a>
            </div>
          </div>

          <!-- Price Section -->
          <div class="price-section">
            <div class="price-display">
              <div class="price-current">
                KES {{ formatPrice(calculateDiscountedPrice()) }}
              </div>

              <div v-if="product.discounted" class="price-original">
                <span class="original-price">
                  KES {{ formatPrice(product.price) }}
                </span>
                <span class="savings-percent">
                  Save {{ Math.round(product.discount_price) }}%
                </span>
              </div>
            </div>

            <div v-if="product.discounted" class="price-saving">
              <i class="fa-solid fa-wallet"></i>
              You save KES
              {{ formatPrice(product.price - calculateDiscountedPrice()) }}
            </div>

            <div class="tax-info">
              <i class="fa-solid fa-info-circle"></i>
              Prices include VAT. Shipping calculated at checkout.
            </div>
          </div>

          <!-- Stock Status -->
          <div class="stock-section">
            <div
              class="stock-status"
              :class="{ 'out-of-stock': product.stock_quantity < 1 }"
            >
              <div class="stock-icon">
                <i
                  class="fa-solid"
                  :class="
                    product.stock_quantity > 0
                      ? 'fa-check-circle'
                      : 'fa-times-circle'
                  "
                ></i>
              </div>

              <div class="stock-details">
                <div class="stock-text">
                  {{
                    product.stock_quantity > 0
                      ? "In Stock"
                      : "Out of Stock"
                  }}
                </div>

                <div class="stock-quantity">
                  {{
                    product.stock_quantity > 0
                      ? `${product.stock_quantity} units available`
                      : "Check back soon"
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Product Description -->
          <div class="description-section">
            <h3 class="section-title">Product Description</h3>
            <p class="product-description">{{ product.short_description || product.description }}</p>
          </div>

          <!-- Full Description -->
          <div v-if="fullDescription" class="description-section description-section--full">
            <h3 class="section-title">Full Description</h3>
            <div class="product-description product-description--full">
              {{ fullDescription }}
            </div>
          </div>

          <!-- Quantity & Actions -->
          <div class="action-section">
            <div
              class="quantity-selector"
              v-if="product.stock_quantity > 0"
            >
              <div class="quantity-label">Quantity:</div>

              <div class="quantity-control">
                <button
                  class="qty-btn qty-minus"
                  @click="decreaseQty"
                  :disabled="getQuantity() <= 1"
                  aria-label="Decrease quantity"
                >
                  <i class="fa-solid fa-minus"></i>
                </button>

                <input
                  type="number"
                  :value="getQuantity()"
                  @input="updateQuantity($event)"
                  class="qty-input"
                  min="1"
                  :max="product.stock_quantity"
                  aria-label="Quantity"
                />

                <button
                  class="qty-btn qty-plus"
                  @click="increaseQty"
                  :disabled="getQuantity() >= product.stock_quantity"
                  aria-label="Increase quantity"
                >
                  <i class="fa-solid fa-plus"></i>
                </button>
              </div>
            </div>

            <div class="action-buttons">
              <button
                v-if="!isInCart()"
                class="btn btn-primary btn-add-cart"
                @click="addToCart"
                :disabled="product.stock_quantity < 1"
              >
                <i class="fa-solid fa-cart-shopping"></i>
                Add to Cart
              </button>

              <div v-else class="cart-added">
                <div class="added-message">
                  <i class="fa-solid fa-check-circle"></i>
                  Added to Cart
                </div>

                <div class="cart-actions">
                  <button class="btn btn-secondary btn-view-cart" @click="goToCart">
                    View Cart
                  </button>

                  <button class="btn btn-outline btn-continue" @click="continueShopping">
                    Continue Shopping
                  </button>
                </div>
              </div>

              <button
                class="btn btn-wishlist"
                @click="addToWishlist"
                :disabled="isAddingToWishlist"
                :class="{ active: isInWishlist }"
              >
                <i
                  class="fa-solid"
                  :class="isInWishlist ? 'fa-heart-circle-check' : 'fa-heart'"
                ></i>
                {{ isInWishlist ? "In Wishlist" : "Add to Wishlist" }}
              </button>
            </div>
          </div>

          <!-- Product Features -->
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-truck-fast"></i>
              </div>
              <div class="feature-content">
                <h4>Free Delivery</h4>
                <p>Free Nairobi CBD delivery within 2–4 hours</p>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-rotate-left"></i>
              </div>
              <div class="feature-content">
                <h4>Easy Returns</h4>
                <p>14-day return policy</p>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-shield-halved"></i>
              </div>
              <div class="feature-content">
                <h4>Quality Guarantee</h4>
                <p>100% authentic products</p>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon">
                <i class="fa-solid fa-headset"></i>
              </div>
              <div class="feature-content">
                <h4>Support</h4>
                <p>24/7 customer support</p>
              </div>
            </div>
          </div>

          <!-- Payment Options -->
          <div class="payment-section">
            <p class="payment-label">Pay with</p>
            <div class="payment-methods">
              <div
                v-for="method in paymentMethods"
                :key="method.name"
                class="payment-method"
                :data-label="method.name"
                :title="method.name"
              >
                <img :src="method.image" :alt="method.name" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Tabs -->
      <div class="product-tabs">
        <div class="tabs-header">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['tab-button', { active: activeTab === tab.id }]"
            @click="activeTab = tab.id"
          >
            <span class="tab-button__icon" aria-hidden="true">
              <i :class="tab.icon"></i>
            </span>
            {{ tab.label }}
          </button>
        </div>

        <div class="tabs-content">
          <!-- SPECIFICATIONS TAB -->
          <transition name="tab-fade" mode="out-in">
          <div v-if="activeTab === 'specs'" key="specs" class="tab-pane">
            <div class="specs-grid">
              <div class="spec-item spec-item--brand">
                <span class="spec-label">
                  <span class="spec-icon" aria-hidden="true">
                    <i class="fa-solid fa-tag"></i>
                  </span>
                  Brand
                </span>
                <span class="spec-value">{{ product.brand }}</span>
              </div>

              <div class="spec-item spec-item--category">
                <span class="spec-label">
                  <span class="spec-icon" aria-hidden="true">
                    <i class="fa-solid fa-layer-group"></i>
                  </span>
                  Category
                </span>
                <span class="spec-value">{{ product.category }}</span>
              </div>

              <div
                class="spec-item"
                :class="product.stock_quantity > 0 ? 'spec-item--stock-in' : 'spec-item--stock-out'"
              >
                <span class="spec-label">
                  <span class="spec-icon" aria-hidden="true">
                    <i
                      :class="
                        product.stock_quantity > 0
                          ? 'fa-solid fa-boxes-stacked'
                          : 'fa-solid fa-box-open'
                      "
                    ></i>
                  </span>
                  Stock Status
                </span>
                <span
                  class="spec-value"
                  :class="{
                    'in-stock': product.stock_quantity > 0,
                    'out-stock': product.stock_quantity <= 0
                  }"
                >
                  {{ product.stock_quantity > 0 ? "In Stock" : "Out of Stock" }}
                </span>
              </div>

              <div class="spec-item spec-item--condition">
                <span class="spec-label">
                  <span class="spec-icon" aria-hidden="true">
                    <i class="fa-solid fa-certificate"></i>
                  </span>
                  Condition
                </span>
                <span class="spec-value">Brand New</span>
              </div>

            </div>
          </div>

          <!-- SHIPPING TAB -->
          <div v-else-if="activeTab === 'shipping'" key="shipping" class="tab-pane">
            <div class="shipping-options">
              <div class="shipping-option">
                <div class="shipping-icon">
                  <i class="fa-solid fa-gift"></i>
                </div>

                <div class="shipping-content">
                  <h4>Free Nairobi CBD Delivery</h4>
                  <p>Free delivery within 2–4 hours for orders in Nairobi CBD</p>

                  <ul class="shipping-areas">
                    <li v-for="area in ['Nairobi CBD', 'Westlands', 'Kilimani', 'Parklands', 'Upper Hill']" :key="area">
                      <i class="fa-solid fa-location-dot" aria-hidden="true"></i>
                      <span>{{ area }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="shipping-option">
                <div class="shipping-icon">
                  <i class="fa-solid fa-truck"></i>
                </div>

                <div class="shipping-content">
                  <h4>Nairobi County</h4>
                  <p>KES 250 • 1–2 business days</p>
                </div>
              </div>
            </div>
          </div>

          <!-- REVIEWS TAB -->
          <div v-else-if="activeTab === 'reviews'" key="reviews" class="tab-pane" id="reviews">

            <!-- Reviews Header -->
            <div class="reviews-header">
              <div class="reviews-summary">
                <div class="average-rating">
                  <div class="rating-number">{{ averageRating }}</div>

                  <div class="stars-large">
                    <i
                      v-for="n in 5"
                      :key="n"
                      class="fa-star"
                      :class="getStarClass(averageRating, n)"
                    ></i>
                  </div>

                  <div class="rating-count">
                    Based on
                    {{ reviewCount }}
                    {{ reviewCount === 1 ? "review" : "reviews" }}
                  </div>
                </div>

                <!-- Rating Distribution -->
                <div v-if="reviewCount > 0" class="rating-distribution">
                  <div
                    v-for="rating in [5, 4, 3, 2, 1]"
                    :key="rating"
                    class="rating-bar"
                  >
                    <span class="rating-label">
                      {{ rating }} star{{ rating > 1 ? "s" : "" }}
                    </span>

                    <div class="bar-container">
                      <div
                        class="bar-fill"
                        :style="{
                          width: `${(ratingDistribution[rating] / reviewCount) * 100}%`
                        }"
                      ></div>
                    </div>

                    <span class="rating-count">
                      {{ ratingDistribution[rating] }}
                    </span>
                  </div>
                </div>

                <div v-else class="no-distribution">
                  <p>Be the first to rate this product!</p>
                </div>
              </div>

              <!-- Review Actions -->
              <div class="review-actions">
                <!-- Show "Login to Review" button if user is not logged in -->
                <button
                  v-if="!isLoggedIn"
                  class="btn btn-outline btn-write-review"
                  @click="goToLogin"
                >
                  <i class="fa-solid fa-sign-in-alt"></i>
                  Login to Review
                </button>

                <!-- Show "Write a Review" button if logged in and hasn't reviewed -->
                <button
                  v-else-if="!userHasReviewed"
                  class="btn btn-primary btn-write-review"
                  @click="showReviewForm = true"
                >
                  <i class="fa-solid fa-pen"></i>
                  Write a Review
                </button>

                <!-- Show "Already Reviewed" message if logged in and has reviewed -->
                <div v-else-if="userHasReviewed" class="already-reviewed">
                  <i class="fa-solid fa-check-circle"></i>
                  You've already reviewed this product
                </div>
              </div>
            </div>

            <!-- Review Form -->
            <div v-if="showReviewForm" class="review-form-container">
              <div class="review-form">
                <div class="form-header">
                  <h4>Write Your Review</h4>

                  <button class="close-btn" @click="cancelReview" aria-label="Close review form">
                    <i class="fa-solid fa-times"></i>
                  </button>
                </div>

                <div class="form-group">
                  <label>Your Rating:</label>

                  <div class="star-rating">
                    <i
                      v-for="star in 5"
                      :key="star"
                      class="fa-star"
                      :class="star <= newReview.rating ? 'fa-solid filled' : 'fa-regular'"
                      @click="setRating(star)"
                    ></i>

                    <span class="rating-text">
                      {{ newReview.rating }} star{{ newReview.rating > 1 ? "s" : "" }}
                    </span>
                  </div>
                </div>

                <div class="form-group">
                  <input
                    type="text"
                    v-model="newReview.title"
                    placeholder="Review title (e.g., 'Great product!')"
                    class="form-input"
                    maxlength="100"
                  />
                  <div class="char-count">{{ newReview.title.length }}/100</div>
                </div>

                <div class="form-group">
                  <textarea
                    v-model="newReview.text"
                    placeholder="Share your experience with this product..."
                    class="form-textarea"
                    rows="4"
                    maxlength="1000"
                  ></textarea>

                  <div class="char-count">{{ newReview.text.length }}/1000</div>
                </div>

                <div class="form-actions">
                  <button class="btn btn-outline" @click="cancelReview">
                    Cancel
                  </button>

                  <button
                    class="btn btn-primary"
                    @click="submitReview"
                    :disabled="!newReview.rating || !newReview.title.trim() || !newReview.text.trim()"
                  >
                    Submit Review
                  </button>
                </div>
              </div>
            </div>

            <!-- Reviews List -->
            <div class="reviews-list">
              <div class="reviews-filter">
                <h5>Filter Reviews:</h5>

                <div class="filter-buttons">
                  <button
                    v-for="filter in reviewFilters"
                    :key="filter.label"
                    :class="['filter-btn', { active: reviewFilter === filter.value }]"
                    @click="reviewFilter = filter.value"
                  >
                    {{ filter.label }}
                    <span
                      v-if="filter.value !== 'all'"
                      class="filter-count"
                    >
                      ({{ ratingDistribution[filter.value] || 0 }})
                    </span>
                  </button>
                </div>
              </div>

              <div v-if="filteredReviews.length === 0" class="no-reviews">
                <i class="fa-solid fa-comment-slash"></i>

                <p v-if="reviewFilter === 'all'">
                  No reviews yet. Be the first to review this product!
                </p>

                <p v-else>No reviews match your filter</p>
              </div>

              <div v-else class="reviews-grid">
                <div
                  v-for="review in filteredReviews"
                  :key="review.id"
                  class="review-card"
                >
                  <!-- Review header -->
                  <div class="review-header">
                    <div class="reviewer-avatar">
                      {{
                        review.user_name
                          ? review.user_name.charAt(0).toUpperCase()
                          : "A"
                      }}
                    </div>

                    <div class="reviewer-info">
                      <div class="reviewer-name">
                        {{ review.user_name || "Anonymous" }}
                      </div>
                      <div class="review-date">
                        {{ formatDate(review.created_at) }}
                      </div>
                    </div>

                    <div class="review-rating">
                      <i
                        v-for="n in 5"
                        :key="n"
                        class="fa-star"
                        :class="n <= review.rating ? 'fa-solid filled' : 'fa-regular'"
                      ></i>
                    </div>
                  </div>

                  <!-- Title -->
                  <h5 class="review-title">{{ review.title }}</h5>

                  <!-- Content -->
                  <p class="review-content">
                    {{ review.text || "No review text provided" }}
                  </p>

                  <!-- Footer -->
                  <div class="review-footer">
                    <button
                      class="helpful-btn"
                      @click="markHelpful(review.id)"
                      v-if="isLoggedIn"
                      :disabled="hasVotedHelpful(review.id)"
                      :class="{ 'voted': hasVotedHelpful(review.id) }"
                    >
                      <i class="fa-solid fa-thumbs-up"></i>
                      Helpful ({{ review.helpful_count || 0 }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </transition>
        </div>
      </div>

      <!-- Related Products: only shown when relatedProducts.length > 0 -->
      <div v-if="relatedProducts.length" class="related-products">
        <h3 class="section-title">You Might Also Like</h3>

        <div class="related-products-grid">
          <div
            v-for="rp in relatedProducts"
            :key="rp.item_id"
            class="related-product-card"
          >
            <div class="related-product-image-wrapper" @click="viewRelatedProduct(rp)">
              <img
                :src="rp.thumbnail || placeholderImageUrl"
                :alt="rp.name"
                class="related-product-image"
                loading="lazy"
                decoding="async"
                @error="handleRelatedImageError($event)"
              />
              <div v-if="rp.discounted" class="related-product-badge">
                {{ Math.round(rp.discount_price) }}% OFF
              </div>
            </div>

            <div class="related-product-info">
              <h4 class="related-product-name">{{ rp.name }}</h4>
              <p v-if="rp.brand" class="related-product-brand">{{ rp.brand }}</p>
              <div class="related-product-price">
                <span v-if="rp.discounted" class="related-original-price">
                  KES {{ formatPrice(rp.price) }}
                </span>
                <span class="related-current-price">
                  KES {{ formatPrice(relatedProductPrice(rp)) }}
                </span>
              </div>
              <button class="btn btn-outline btn-learn-more" @click="viewRelatedProduct(rp)">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ThirdpartyFooter />
  </div>
</template>

<script>
/**
 * ProductView (ProductViewer)
 *
 * Full product detail page with:
 * - Image gallery, pricing, stock status, cart/wishlist actions
 * - Specifications, shipping, and reviews tabs
 * - Related products section (same brand + category)
 *
 * Route: /ipromiseafrica/store/product/:slug (URL stays slug-based for SEO/sharing).
 * Loads catalog data via GET /post/products/slug/{slug} (same payload as by item_id).
 * Related products use GET /post/products/item/{item_id}/related.
 *
 * @component
 * @requires MainNavbar
 * @requires useCartStore
 */
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "../thirdparty/Footer.vue";
import { useCartStore } from "../../cartstore/cartStore";
import {
  buildCartProductPayload,
  findCartLine,
  COLOR_VARIANT_SOURCE,
  orderedVariantViews,
  primaryVariantImage,
} from "../../cartstore/colorVariants";
import { buildApiUrl } from "@/config/api";
import mpesaLogo from "@/assets/images/payment/mpesa-logo.png";
import bankTransferLogo from "@/assets/images/payment/rtgs.png";
import cardLogo from "@/assets/images/payment/visa.png";
import cashLogo from "@/assets/images/payment/cash.png";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import globalToastMixin from "@/mixins/globalToastMixin";

export default {
  name: "ProductViewer",
  loadingMessage: 'Loading product details...',
  mixins: [globalToastMixin, globalLoadingMixin],
  components: { MainNavbar, ThirdpartyFooter },

  data() {
    return {
      product: null,
      loading: true,
      activeImage: "",
      activeTab: "specs",
      currentImageIndex: 0,
      showReviewForm: false,

      newReview: {
        rating: 0,
        title: "",
        text: ""
      },

      reviews: [],
      isAddingToWishlist: false,
      isInWishlist: false,
      reviewFilter: "all",

      tabs: [
        { id: "specs", label: "Specifications", icon: "fa-solid fa-list-check" },
        { id: "shipping", label: "Shipping Info", icon: "fa-solid fa-truck-fast" },
        { id: "reviews", label: "Reviews", icon: "fa-solid fa-comment-dots" }
      ],

      paymentMethods: [
        { name: "M-Pesa", image: mpesaLogo },
        { name: "Bank Transfer", image: bankTransferLogo },
        { name: "Debit/Credit Card", image: cardLogo },
        { name: "Cash on Delivery", image: cashLogo },
      ],

      reviewFilters: [
        { label: "All Reviews", value: "all" },
        { label: "5 Stars", value: 5 },
        { label: "4 Stars", value: 4 },
        { label: "3 Stars", value: 3 },
        { label: "2 Stars", value: 2 },
        { label: "1 Star", value: 1 }
      ],

      /** Related products (same brand + category). Empty = section hidden. */
      relatedProducts: [],

      /** Placeholder for broken/missing images. */
      placeholderImageUrl:
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==",

      /** Track if current user has reviewed this product */
      userHasReviewed: false,

      /** Track helpful votes to prevent duplicate votes */
      helpfulVotes: new Set(), // Set of review IDs the user has voted helpful on

      // ── Color variants (footwear) ──────────────────────────────────────
      /** color_variants array from API, or [] when not a footwear product */
      colorVariants: [],
      /** id of the color the user has committed to (clicked) */
      committedColorId: null,
      /** id of the color being previewed by hover; null when not hovering */
      previewColorId: null,
    };
  },

  computed: {
    isFirstImage() {
      return this.currentImageIndex === 0;
    },
    isLastImage() {
      return (
        this.product &&
        this.currentImageIndex === this.product.images.length - 1
      );
    },

    isLoggedIn() {
      return !!localStorage.getItem("access_token");
    },

    averageRating() {
      if (!this.reviews.length) return 0;
      const sum = this.reviews.reduce((t, r) => t + r.rating, 0);
      return parseFloat((sum / this.reviews.length).toFixed(1));
    },

    reviewCount() {
      return this.reviews.length;
    },

    filteredReviews() {
      if (this.reviewFilter === "all") return this.reviews;
      return this.reviews.filter(
        (review) => review.rating === parseInt(this.reviewFilter)
      );
    },

    ratingDistribution() {
      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

      if (!this.reviews.length) return distribution;

      this.reviews.forEach((review) => {
        if (review.rating >= 1 && review.rating <= 5) {
          distribution[review.rating]++;
        }
      });

      return distribution;
    },

    fullDescription() {
      if (!this.product) return "";
      return (this.product.description || "").trim();
    },

    // ── Color-variant helpers ──────────────────────────────────────────
    hasColorVariants() {
      return this.colorVariants.length > 0;
    },

    /** The color id that is currently driving the main image display */
    displayColorId() {
      return this.previewColorId ?? this.committedColorId;
    },

    /** The full variant object being displayed right now */
    displayVariant() {
      if (!this.displayColorId) return null;
      return this.colorVariants.find((v) => v.id === this.displayColorId) ?? null;
    },

    /** The committed variant – drives the view thumbnail strip */
    activeVariant() {
      if (!this.committedColorId) return null;
      return this.colorVariants.find((v) => v.id === this.committedColorId) ?? null;
    },

    /**
     * Views of the committed color. Hidden when a color has only one image.
     * Known footwear and apparel views stay in a stable order; any future
     * view key from the API is appended.
     */
    activeVariantViews() {
      if (!this.activeVariant) return [];
      const items = orderedVariantViews(this.activeVariant.views);
      if (items.length <= 1) return [];
      return items;
    },
  },

  setup() {
    const cartStore = useCartStore();
    return { cartStore };
  },

  methods: {
    getStarClass(rating, index) {
      if (index <= rating) return "fa-solid filled";
      if (index - 0.5 <= rating) return "fa-solid fa-star-half-alt";
      return "fa-regular";
    },

    calculateDiscountedPrice() {
      if (this.product?.discounted && this.product?.discount_price) {
        const discountPercentage = parseFloat(this.product.discount_price) / 100;
        return Math.round(this.product.price * (1 - discountPercentage));
      }
      return this.product?.price || 0;
    },

    formatPrice(price) {
      if (!price) return "0";
      return Number(price).toLocaleString("en-US");
    },

    /** Align with API _validate_slug: lowercase for stable URLs and cache keys. */
    normalizeProductSlug(slug) {
      if (slug == null || slug === "") return "";
      return String(slug).trim().toLowerCase();
    },

    async fetchProduct() {
      this.loading = true;
      try {
        const slug = this.normalizeProductSlug(this.$route.params.slug);
        if (!slug) throw new Error("Product slug is required");

        const response = await fetch(
          buildApiUrl(`/post/products/slug/${encodeURIComponent(slug)}`)
        );

        if (!response.ok) throw new Error("Failed to fetch product");

        const data = await response.json();
        this.product = {
          ...data.product,
          short_description: data.product?.short_description || "",
          description:
            data.product?.description ||
            data.product?.short_description ||
            "",
        };

        // ── Initialise color variants ───────────────────────────────
        const variants = Array.isArray(data.product.color_variants)
          ? data.product.color_variants.filter(
              (v) => v && v.id && primaryVariantImage(v)
            )
          : [];
        this.colorVariants = variants;

        if (variants.length > 0) {
          // Select the first colorway by default
          this.committedColorId = variants[0].id;
          this.previewColorId = null;
          this.activeImage = this.colorHoverImage(variants[0]);
        } else {
          this.committedColorId = null;
          this.previewColorId = null;
          this.activeImage =
            data.product.thumbnail ||
            (data.product.images && data.product.images[0]) ||
            "";
        }

        this.currentImageIndex = 0;

        this.reviews = Array.isArray(data.product.reviews)
          ? data.product.reviews.map((review) => ({
              ...review,
              text: review.text || review.review_text || "No review text provided"
            }))
          : [];

        await this.checkWishlistStatus();
        await this.fetchRelatedProducts();
        await this.checkUserReviewStatus();
      } catch (err) {
        console.error("Error fetching product:", err);
        this.product = null;
        this.reviews = [];
        this.relatedProducts = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch related products (same brand + category).
     * Section is hidden when relatedProducts.length === 0.
     */
    async fetchRelatedProducts() {
      const itemId = this.product?.item_id;
      if (itemId == null || !this.product) {
        this.relatedProducts = [];
        return;
      }
      try {
        const res = await fetch(
          buildApiUrl(
            `/post/products/item/${encodeURIComponent(String(itemId))}/related`
          )
        );
        if (!res.ok) return;
        const data = await res.json();
        this.relatedProducts = Array.isArray(data.products) ? data.products : [];
      } catch {
        this.relatedProducts = [];
      }
    },

    /** Navigate to related product detail by slug. */
    viewRelatedProduct(product) {
      const s = this.normalizeProductSlug(product?.slug);
      if (s) {
        this.$router.push({ name: "ProductViewer", params: { slug: s } });
      }
    },

    async checkWishlistStatus() {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const response = await fetch(buildApiUrl("/wishlist/items"), {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.ok) {
          const wishlistItems = await response.json();
          this.isInWishlist = wishlistItems.some(
            (item) => item.product_id === this.product.item_id
          );
        }
      } catch (error) {
        console.error("Error checking wishlist status:", error);
      }
    },

    async checkUserReviewStatus() {
      if (!this.isLoggedIn || !this.product) {
        this.userHasReviewed = false;
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.userHasReviewed = false;
          return;
        }

        const response = await fetch(
          buildApiUrl(`/review/check/${this.product.item_id}`),
          {
            headers: {
              Authorization: `Bearer ${token.trim()}`
            }
          }
        );

        // Handle 401 - token expired or invalid
        if (response.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("user_data");
          this.userHasReviewed = false;
          return;
        }

        if (response.ok) {
          const data = await response.json();
          this.userHasReviewed = data.has_reviewed || false;
        } else {
          // Fallback to local check
          const userId = this.getCurrentUserId();
          this.userHasReviewed = this.reviews.some((r) => r.user_id === userId);
        }
      } catch (error) {
        console.error("Error checking review status:", error);
        // Fallback to local check
        const userId = this.getCurrentUserId();
        this.userHasReviewed = this.reviews.some((r) => r.user_id === userId);
      }
    },

    async addToWishlist() {
      if (!this.isLoggedIn) return this.goToLogin();

      const token = localStorage.getItem("access_token");
      if (!token) return this.goToLogin();

      this.isAddingToWishlist = true;

      try {
        const wishlistPayload = {
          product_id: this.product.item_id,
          product_name: this.product.name,
          product_brand: this.product.brand,
          product_price: this.product.price,
          product_discount_price: this.product.discount_price,
          product_discounted: this.product.discounted,
          product_thumbnail: this.product.thumbnail
        };

        const response = await fetch(buildApiUrl("/wishlist/add"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(wishlistPayload)
        });

        if (!response.ok) throw new Error("Failed to add to wishlist");

        this.isInWishlist = true;
        this.showToastMessage("Success", "Added to wishlist!", "success");
      } catch (error) {
        console.error("Error adding to wishlist:", error);
        this.showToastMessage("Error", "Failed to add to wishlist", "error");
      } finally {
        this.isAddingToWishlist = false;
      }
    },

    switchToImage(img, index) {
      this.activeImage = img;
      this.currentImageIndex = index;
    },

    swatchColor(value) {
      const raw = String(value || "").trim();
      if (!raw) return "transparent";
      return raw.startsWith("#") ? raw : `#${raw}`;
    },

    isApparelProduct() {
      const sub = String(
        this.product?.sub_category || this.product?.subcategory || ""
      ).toLowerCase();
      return sub === "apparel";
    },

    /**
     * Image shown when a color is hovered or first selected.
     * Footwear uses main. Apparel uses undressed.
     */
    colorHoverImage(variant) {
      const views = variant?.views || {};
      if (this.isApparelProduct()) {
        return views.undressed || primaryVariantImage(variant);
      }
      return views.main || primaryVariantImage(variant);
    },

    // ── Color-variant interaction ──────────────────────────────────────
    /** User hovers a swatch – preview main (footwear) or undressed (apparel) */
    onSwatchHover(colorId) {
      if (colorId === this.committedColorId) return;
      this.previewColorId = colorId;
      const v = this.colorVariants.find((x) => x.id === colorId);
      const hero = this.colorHoverImage(v);
      if (hero) this.activeImage = hero;
    },

    /** Mouse leaves swatch area – revert to committed color's main or undressed view */
    onSwatchLeave() {
      this.previewColorId = null;
      const committed = this.colorVariants.find(
        (x) => x.id === this.committedColorId
      );
      const hero = this.colorHoverImage(committed);
      if (hero) this.activeImage = hero;
    },

    /** User clicks a swatch – commit that color and show main or undressed */
    onSwatchClick(colorId) {
      this.committedColorId = colorId;
      this.previewColorId = null;
      const v = this.colorVariants.find((x) => x.id === colorId);
      const hero = this.colorHoverImage(v);
      if (hero) this.activeImage = hero;
    },

    /** User clicks a view thumbnail in the strip */
    onVariantViewClick(url) {
      if (url) this.activeImage = url;
    },

    nextImage() {
      if (
        this.product.images &&
        this.currentImageIndex < this.product.images.length - 1
      ) {
        this.currentImageIndex++;
        this.activeImage = this.product.images[this.currentImageIndex];
      }
    },

    previousImage() {
      if (this.product.images && this.currentImageIndex > 0) {
        this.currentImageIndex--;
        this.activeImage = this.product.images[this.currentImageIndex];
      }
    },

    handleImageError(event) {
      event.target.src = this.placeholderImageUrl;
    },

    handleRelatedImageError(event) {
      event.target.src = this.placeholderImageUrl;
    },

    /** Compute displayed price for related product (discounted or regular). */
    relatedProductPrice(rp) {
      if (rp?.discounted && rp?.discount_price) {
        const pct = parseFloat(rp.discount_price) / 100;
        return Math.round((rp.price || 0) * (1 - pct));
      }
      return rp?.price || 0;
    },

    goToCart() {
      this.$router.push({ name: "CartSection" });
    },

    continueShopping() {
      this.$router.push({ name: "MainPage" });
    },

    setRating(rating) {
      this.newReview.rating = rating;
    },

    async submitReview() {
      if (!this.isLoggedIn) return this.goToLogin();

      if (!this.newReview.rating)
        return this.showToastMessage(
          "Rating Required",
          "Please select a rating",
          "error"
        );

      if (!this.newReview.title.trim())
        return this.showToastMessage(
          "Title Required",
          "Please enter a review title",
          "error"
        );

      if (!this.newReview.text.trim())
        return this.showToastMessage(
          "Review Required",
          "Please enter your review text",
          "error"
        );

      try {
        // Check if token exists
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToastMessage(
            "Authentication Required",
            "Please log in to submit a review",
            "error"
          );
          this.goToLogin();
          return;
        }

        const productType = this.product.category_type;
        if (!productType) throw new Error("Product type not found");

        const typeToField = {
          football_items: "football_item_id",
          rugby_items: "rugby_item_id",
          basketball_items: "basketball_item_id",
          volleyball_items: "volleyball_item_id",
          athletics_running_items: "athletics_item_id",
          gym_fitness_items: "gym_item_id",
          racket_sports: "racket_item_id",
          cricket_items: "cricket_item_id",
          archery: "archery_item_id",
          swimming_items: "swimming_item_id",
          hockey_items: "hockey_item_id",
          combat_sport: "combat_item_id",
          cycling_items: "cycling_item_id",
          equestrian: "equestrian_item_id",
          golf_items: "golf_item_id",
          adventureandoutdoor: "adventure_item_id",
          niche_regional_games: "niche_item_id"
        };

        const fieldName = typeToField[productType];
        if (!fieldName)
          throw new Error("Cannot submit review for this product type");

        const response = await fetch(
          buildApiUrl("/review/user-review"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token.trim()}`
            },
            body: JSON.stringify({
              rating: this.newReview.rating,
              title: this.newReview.title.trim(),
              text: this.newReview.text.trim(),
              [fieldName]: this.product.item_id
            })
          }
        );

        if (!response.ok) {
          let errorMessage = "Failed to submit review";
          
          try {
            const error = await response.json();
            errorMessage = error.detail || error.message || errorMessage;
          } catch (e) {
            // If response is not JSON, use status text
            errorMessage = response.statusText || errorMessage;
          }
          
          // Handle authentication errors
          if (response.status === 401) {
            this.showToastMessage(
              "Authentication Required",
              "Your session has expired. Please log in again.",
              "error"
            );
            // Clear invalid token and redirect to login
            localStorage.removeItem("access_token");
            localStorage.removeItem("user_data");
            setTimeout(() => {
              this.goToLogin();
            }, 2000);
            return;
          }
          
          // Handle duplicate review error specifically
          if (response.status === 409 || errorMessage.includes("already reviewed")) {
            this.showToastMessage(
              "Already Reviewed",
              "You have already reviewed this product. Each user can only review once.",
              "error"
            );
            // Refresh product to update review status
            await this.fetchProduct();
            return;
          }
          
          throw new Error(errorMessage);
        }

        await response.json();

        this.showToastMessage(
          "Review Submitted Successfully!",
          "Thank you for your feedback! Your review is pending approval and will be visible once approved by our team.",
          "success"
        );

        this.cancelReview();
        // Refresh product to get updated reviews list
        await this.fetchProduct();
      } catch (error) {
        console.error("Error submitting review:", error);
        
        // Provide more specific error messages
        let errorTitle = "Failed to Submit Review";
        let errorMessage = error.message || "An unexpected error occurred. Please try again.";
        
        if (error.message.includes("Rate limit")) {
          errorTitle = "Too Many Reviews";
          errorMessage = "You've submitted too many reviews recently. Please wait a bit before submitting another review.";
        } else if (error.message.includes("spam")) {
          errorTitle = "Review Content Issue";
          errorMessage = "Your review contains patterns that may be considered spam. Please write a genuine, original review.";
        } else if (error.message.includes("already reviewed")) {
          errorTitle = "Already Reviewed";
          errorMessage = "You have already reviewed this product. Each user can only submit one review per product.";
        } else if (error.message.includes("Authentication") || error.message.includes("session")) {
          errorTitle = "Session Expired";
          errorMessage = "Your session has expired. Please log in again to submit your review.";
        }
        
        this.showToastMessage(errorTitle, errorMessage, "error");
      }
    },

    cancelReview() {
      this.newReview = { rating: 0, title: "", text: "" };
      this.showReviewForm = false;
    },

    formatDate(dateString) {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diff === 0) return "Today";
        if (diff === 1) return "Yesterday";
        if (diff < 7) return `${diff} days ago`;

        if (diff < 30) {
          const weeks = Math.floor(diff / 7);
          return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
        }

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch {
        return "";
      }
    },

    getCurrentUserId() {
      try {
        const data = JSON.parse(localStorage.getItem("user_data"));
        return data?.customer_number || data?.id || null;
      } catch {
        return null;
      }
    },

    getCurrentUserName() {
      try {
        const data = JSON.parse(localStorage.getItem("user_data"));
        return data?.full_name || data?.name || "Anonymous";
      } catch {
        return "Anonymous";
      }
    },

    /**
     * Load helpful votes from localStorage on component mount.
     */
    loadHelpfulVotes() {
      try {
        const stored = localStorage.getItem("helpful_votes");
        if (stored) {
          const votes = JSON.parse(stored);
          if (Array.isArray(votes)) {
            this.helpfulVotes = new Set(votes);
          }
        }
      } catch (e) {
        console.error("Error loading helpful votes:", e);
        this.helpfulVotes = new Set();
      }
    },

    /**
     * Check if user has already voted helpful on a review.
     * Uses localStorage to persist across page refreshes.
     */
    hasVotedHelpful(reviewId) {
      if (!reviewId) return false;
      
      // Check in-memory Set first
      if (this.helpfulVotes.has(reviewId)) return true;
      
      // Check localStorage
      try {
        const stored = localStorage.getItem("helpful_votes");
        if (stored) {
          const votes = JSON.parse(stored);
          return Array.isArray(votes) && votes.includes(reviewId);
        }
      } catch (e) {
        console.error("Error reading helpful votes from localStorage:", e);
      }
      
      return false;
    },

    /**
     * Mark a review as helpful by calling the API endpoint.
     * Prevents duplicate votes and updates the UI.
     */
    async markHelpful(reviewId) {
      if (!this.isLoggedIn) {
        this.showToastMessage("Login Required", "Please log in to mark reviews as helpful", "error");
        return;
      }

      if (!reviewId) {
        this.showToastMessage("Error", "Invalid review ID", "error");
        return;
      }

      // Check if already voted
      if (this.hasVotedHelpful(reviewId)) {
        this.showToastMessage("Already Voted", "You have already marked this review as helpful", "info");
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        if (!token || !token.trim()) {
          this.showToastMessage("Authentication Required", "Please log in to mark reviews as helpful", "error");
          this.goToLogin();
          return;
        }

        const response = await fetch(
          buildApiUrl(`/review/${reviewId}/helpful`),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token.trim()}`
            }
          }
        );

        if (!response.ok) {
          let errorMessage = "Failed to mark review as helpful";
          
          try {
            const error = await response.json();
            errorMessage = error.detail || error.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }

          // Handle specific error cases
          if (response.status === 400 && errorMessage.includes("own review")) {
            this.showToastMessage(
              "Cannot Vote",
              "You cannot mark your own review as helpful. Only other users can vote on your reviews.",
              "error"
            );
            return;
          }

          if (response.status === 401) {
            this.showToastMessage(
              "Login Required",
              "Please log in to mark reviews as helpful. Your session may have expired.",
              "error"
            );
            localStorage.removeItem("access_token");
            localStorage.removeItem("user_data");
            setTimeout(() => {
              this.goToLogin();
            }, 2000);
            return;
          }

          if (response.status === 404) {
            this.showToastMessage(
              "Review Not Found",
              "This review no longer exists. The page will refresh to update the reviews list.",
              "error"
            );
            // Refresh product to update reviews
            await this.fetchProduct();
            return;
          }

          if (response.status === 409 && (errorMessage.includes("already") || errorMessage.includes("duplicate"))) {
            this.showToastMessage(
              "Already Voted",
              "You have already marked this review as helpful. Each user can only vote once per review.",
              "info"
            );
            // Update local tracking
            this.helpfulVotes.add(reviewId);
            try {
              const stored = localStorage.getItem("helpful_votes");
              let votes = stored ? JSON.parse(stored) : [];
              if (!Array.isArray(votes)) votes = [];
              if (!votes.includes(reviewId)) {
                votes.push(reviewId);
                localStorage.setItem("helpful_votes", JSON.stringify(votes));
              }
            } catch (e) {
              console.error("Error storing helpful vote:", e);
            }
            return;
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();
        
        // Mark as voted in memory
        this.helpfulVotes.add(reviewId);
        
        // Persist to localStorage
        try {
          const stored = localStorage.getItem("helpful_votes");
          let votes = stored ? JSON.parse(stored) : [];
          if (!Array.isArray(votes)) votes = [];
          if (!votes.includes(reviewId)) {
            votes.push(reviewId);
            // Keep only last 1000 votes to prevent localStorage bloat
            if (votes.length > 1000) {
              votes = votes.slice(-1000);
            }
            localStorage.setItem("helpful_votes", JSON.stringify(votes));
          }
        } catch (e) {
          console.error("Error storing helpful vote:", e);
        }

        // Update the review's helpful count in the UI
        const review = this.reviews.find(r => r.id === reviewId);
        if (review) {
          review.helpful_count = data.helpful_count || (review.helpful_count || 0) + 1;
        }

        this.showToastMessage(
          "Thank You!",
          `Your vote has been recorded. ${data.helpful_count || review?.helpful_count || 0} people found this review helpful.`,
          "success"
        );
      } catch (error) {
        console.error("Error marking review as helpful:", error);
        
        // Provide more specific error messages
        let errorTitle = "Failed to Vote";
        let errorMessage = error.message || "An unexpected error occurred. Please try again.";
        
        if (error.message.includes("already")) {
          errorTitle = "Already Voted";
          errorMessage = "You have already marked this review as helpful. Each user can only vote once per review.";
        } else if (error.message.includes("own review")) {
          errorTitle = "Cannot Vote";
          errorMessage = "You cannot mark your own review as helpful. Only other users can vote on your reviews.";
        } else if (error.message.includes("Authentication") || error.message.includes("session")) {
          errorTitle = "Login Required";
          errorMessage = "Please log in to mark reviews as helpful.";
        } else if (error.message.includes("Rate limit")) {
          errorTitle = "Too Many Votes";
          errorMessage = "You've voted too many times recently. Please wait a bit before voting again.";
        }
        
        this.showToastMessage(errorTitle, errorMessage, "error");
      }
    },

    addToCart() {
      if (!this.product) return;

      try {
        const productData = buildCartProductPayload(this.product, {
          mode: this.hasColorVariants
            ? COLOR_VARIANT_SOURCE.SELECTED
            : COLOR_VARIANT_SOURCE.THUMBNAIL,
          selectedVariant: this.activeVariant,
        });
        productData.currency = this.product.currency || "KES";

        this.cartStore.addToCart(productData);

        const colorNote =
          productData.color_label || productData.color_id
            ? ` (${productData.color_label || productData.color_id})`
            : "";
        this.showToastMessage(
          "Added to Cart",
          `${this.product.name}${colorNote} added to your cart`,
          "success"
        );
      } catch (error) {
        console.error("Error adding to cart:", error);
        this.showToastMessage(
          "Authentication Required",
          error.message || "Please log in to add items to your cart",
          "error"
        );
      }
    },

    /** Cart line ref for the currently committed color (or plain product). */
    currentCartRef() {
      if (!this.product) return null;
      return {
        item_id: this.product.item_id,
        color_id: this.hasColorVariants ? this.committedColorId : null,
      };
    },

    updateQuantity(event) {
      if (!this.product) return;

      try {
        const qty = parseInt(event.target.value) || 1;
        const ref = this.currentCartRef();
        const item = findCartLine(this.cartStore.items, ref);

        if (item) {
          if (qty > 0) {
            this.cartStore.updateQuantity(ref, qty);
          } else {
            this.cartStore.removeFromCart(ref);
          }
        }
      } catch (error) {
        console.error("Error updating quantity:", error);
        this.showToastMessage("Error", "Failed to update quantity", "error");
      }
    },

    increaseQty() {
      try {
        const ref = this.currentCartRef();
        const item = findCartLine(this.cartStore.items, ref);

        if (item) {
          this.cartStore.increaseQty(ref);
        } else {
          this.addToCart();
        }
      } catch (error) {
        console.error("Error increasing quantity:", error);
        this.showToastMessage("Error", "Failed to increase quantity", "error");
      }
    },

    decreaseQty() {
      try {
        const ref = this.currentCartRef();
        const item = findCartLine(this.cartStore.items, ref);

        if (item) {
          this.cartStore.decreaseQty(ref);
        }
      } catch (error) {
        console.error("Error decreasing quantity:", error);
        this.showToastMessage("Error", "Failed to decrease quantity", "error");
      }
    },

    isInCart() {
      const ref = this.currentCartRef();
      if (!ref) return false;
      return !!findCartLine(this.cartStore.items, ref);
    },

    getQuantity() {
      const ref = this.currentCartRef();
      if (!ref) return 0;
      const item = findCartLine(this.cartStore.items, ref);
      return item?.quantity || 0;
    },

    scrollToReviews() {
      this.activeTab = "reviews";

      setTimeout(() => {
        const el = document.getElementById("reviews");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    },

    /**
     * Navigate to login page. Stores current route for redirect after login.
     */
    goToLogin() {
      // Store current route for redirect after login
      const currentRoute = this.$route.fullPath;
      localStorage.setItem("redirect_after_login", currentRoute);
      
      // Navigate to login page
      this.$router.push({ name: "CustomerSignIn" });
    }
  },

  watch: {
    "$route.params.slug"() {
      this.fetchProduct();
    },
  },

  mounted() {
    this.fetchProduct();
    this.loadHelpfulVotes();
  }
};
</script>
<style scoped>
/* ================================
   CSS VARIABLES (Using your existing ones)
   ================================ */
.product-view {
  
  /* Star rating colors */
  --star-filled: #ffc107;
  --star-empty: #dddddd;
  --star-half: linear-gradient(90deg, #ffc107 50%, #dddddd 50%);
  
  /* Status colors */
  --status-success: #2e7d32;
  --status-error: #f44336;
  --status-warning: #ff9800;
  --status-info: #2196f3;
  
  /* Spacing */
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
  --spacing-xxl: 48px;
  
  /* Border radius */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-round: 50%;
  
  /* Transitions */
  --transition-fast: 150ms ease;
  --transition-base: 250ms ease;
  --transition-slow: 350ms ease;
  
  /* Shadows */
  --shadow-sm: 0 2px 4px rgba(0,0,0,0.05);
  --shadow-md: 0 4px 12px rgba(0,0,0,0.1);
  --shadow-lg: 0 8px 24px rgba(0,0,0,0.15);
}

/* ================================
   SHOPIFY-STYLE PRODUCT VIEW
   Clean, Modern, Responsive
   ================================ */

.product-view {
  min-height: 100vh;
  padding-top: 80px;
  background: var(--background-color1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  color: var(--text-color1);
}

/* Match SignUp.vue: footer flush with page content */
.product-view :deep(.thirdparty-footer) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.store-breadcrumb {
  max-width: 1280px;
  margin: 0 auto;
  padding: 18px var(--spacing-lg) 6px;
}

.store-breadcrumb__trail {
  display: flex;
  align-items: center;
  gap: 2px;
  width: fit-content;
  max-width: 100%;
  margin: 0;
  padding: 5px 6px 5px 5px;
  list-style: none;
  border-radius: 999px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover);
  color: var(--text-color1);
  box-shadow: 0 0 0 0 transparent;
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    background 0.3s ease;
}

.store-breadcrumb__trail:hover {
  box-shadow: 0 0 14px color-mix(in srgb, var(--border-color-hover) 45%, transparent);
}

.store-breadcrumb__item {
  display: flex;
  align-items: center;
  min-width: 0;
}

.store-breadcrumb__item--current {
  flex: 1 1 auto;
  min-width: 0;
}

.store-breadcrumb__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 999px;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  transition:
    background 0.3s ease,
    color 0.3s ease;
}

.store-breadcrumb__icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: color-mix(in srgb, var(--primary-color) 22%, transparent);
  color: var(--primary-color);
  font-size: 12px;
  transition:
    transform 0.3s ease,
    background 0.3s ease;
}

.store-breadcrumb__link:hover {
  color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
}

.store-breadcrumb__link:hover .store-breadcrumb__icon {
  transform: scale(1.06);
  background: color-mix(in srgb, var(--primary-color) 32%, transparent);
}

.store-breadcrumb__link:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.store-breadcrumb__sep {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  flex: 0 0 auto;
  color: var(--text-color1);
  font-size: 9px;
  opacity: 0.65;
  user-select: none;
}

.store-breadcrumb__current {
  display: block;
  padding: 8px 12px 8px 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.2;
  color: var(--text-color1);
  max-width: min(52vw, 32rem);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .store-breadcrumb {
    padding-left: var(--spacing-md);
    padding-right: var(--spacing-md);
  }

  .store-breadcrumb__current {
    max-width: 46vw;
  }
}

/* ================================
   PRODUCT LAYOUT
   ================================ */
.product-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg) var(--spacing-xxl);
}

.product-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xxl);
}

@media (max-width: 990px) {
  .product-layout {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
}

@media (max-width: 768px) {
  .product-container {
    padding: 0 var(--spacing-md) var(--spacing-xl);
  }
  
  .product-layout {
    gap: var(--spacing-lg);
    margin-bottom: var(--spacing-xl);
  }
}

/* ================================
   IMAGE GALLERY
   ================================ */
.image-section {
  position: static;
}

@media (max-width: 990px) {
  .image-section {
    margin-top: var(--spacing-lg);
    background: var(--background-color2);
    border-radius: var(--radius-lg);
    padding: var(--spacing-md);
    box-shadow: var(--shadow-sm);
    border: 1px solid var(--border-color-hover1);
  }
}

.main-image-wrapper {
  position: relative;
  margin-bottom: var(--spacing-md);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
}

.main-image-container {
  position: relative;
  aspect-ratio: 1/1;
}

.main-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--spacing-lg);
}

.discount-badge {
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  z-index: 10;
}

.discount-percent {
  background: linear-gradient(135deg, var(--status-error), #ff6b6b);
  color: var(--text-color3);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 20px;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(255, 68, 68, 0.3);
}

.image-navigation {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: linear-gradient(transparent, rgba(0,0,0,0.3));
  z-index: 10;
}

.nav-btn {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-round);
  background: var(--text-color3);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  color: var(--text-color1);
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.nav-btn:hover:not(:disabled) {
  background: var(--border-color-hover);
  color: var(--text-color3);
  transform: scale(1.05);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.image-counter {
  color: var(--text-color3);
  font-weight: 600;
  font-size: 13px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.thumbnail-gallery {
  padding: var(--spacing-sm) 0;
}

.thumbnail-scroll {
  display: flex;
  gap: var(--spacing-sm);
  overflow-x: auto;
  padding: var(--spacing-sm) 0;
  scrollbar-width: thin;
}

.thumbnail-scroll::-webkit-scrollbar {
  height: 4px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb {
  background: var(--border-color-hover1);
  border-radius: 2px;
}

.thumbnail-btn {
  flex: 0 0 auto;
  width: 80px;
  height: 80px;
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 2px solid transparent;
  background: var(--background-color2);
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.thumbnail-btn.active {
  border-color: var(--border-color-hover);
}

.thumbnail-btn:hover:not(.active) {
  border-color: var(--border-color-hover1);
}

.thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ================================
   COLOR VARIANT SWATCHES
   ================================ */
.color-variants-section {
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) 0;
}

.color-variants-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-color2);
  margin-bottom: var(--spacing-sm);
}

.color-variants-name {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--text-color1);
}

.color-swatches-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.color-swatch {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  background: none;
  transition: border-color 0.18s ease, transform 0.15s ease,
    box-shadow 0.18s ease;
  display: flex;
  outline: none;
}

.color-swatch:focus-visible {
  outline: 2px solid var(--primary-color, #1976d2);
  outline-offset: 2px;
}

.color-swatch--committed {
  border-color: var(--text-color1);
  box-shadow: 0 0 0 2px var(--background-color2),
    0 0 0 4px var(--text-color1);
}

.color-swatch--previewing:not(.color-swatch--committed) {
  border-color: var(--border-color-hover);
  transform: scale(1.12);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22);
}

.color-swatch:hover:not(.color-swatch--committed) {
  transform: scale(1.12);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22);
}

.swatch-half {
  display: block;
  width: 50%;
  height: 100%;
}

.swatch-solid {
  display: block;
  width: 100%;
  height: 100%;
}

/* ================================
   VARIANT VIEW STRIP
   ================================ */
.variant-view-strip {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
  overflow-x: auto;
  padding-bottom: var(--spacing-xs, 4px);
  scrollbar-width: thin;
}

.variant-view-strip::-webkit-scrollbar {
  height: 4px;
}

.variant-view-strip::-webkit-scrollbar-thumb {
  background: var(--border-color-hover1);
  border-radius: 2px;
}

.variant-view-btn {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: var(--background-color2);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  padding: 4px;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.15s ease;
  outline: none;
}

.variant-view-btn:focus-visible {
  outline: 2px solid var(--primary-color, #1976d2);
  outline-offset: 2px;
}

.variant-view-btn:hover {
  border-color: var(--border-color-hover1);
  transform: translateY(-2px);
}

.variant-view-btn--active {
  border-color: var(--border-color-hover);
}

.variant-view-thumb {
  width: 70px;
  height: 70px;
  object-fit: contain;
  border-radius: calc(var(--radius-md) - 4px);
}

.variant-view-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color2);
}

@media (max-width: 768px) {
  .color-swatch {
    width: 30px;
    height: 30px;
  }

  .variant-view-thumb {
    width: 58px;
    height: 58px;
  }
}

@media (max-width: 480px) {
  .color-swatch {
    width: 28px;
    height: 28px;
  }

  .variant-view-thumb {
    width: 52px;
    height: 52px;
  }
}

@media (max-width: 768px) {
  .main-image-wrapper {
    border-radius: var(--radius-md);
  }
  
  .main-image-container {
    aspect-ratio: 4/3;
  }
  
  .thumbnail-btn {
    width: 60px;
    height: 60px;
  }
}

@media (max-width: 480px) {
  .image-section {
    padding: var(--spacing-sm);
    margin-top: var(--spacing-md);
  }
  
  .main-image-container {
    aspect-ratio: 1/1;
  }
  
  .thumbnail-btn {
    width: 50px;
    height: 50px;
  }
}

/* ================================
   PRODUCT DETAILS
   ================================ */
.details-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.product-header {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.product-badges {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-discount {
  background: linear-gradient(135deg, var(--status-error), #ff6b6b);
  color: var(--text-color3);
}

.badge-warning {
  background: linear-gradient(135deg, var(--status-warning), #ff9800);
  color: var(--text-color3);
}

.badge-shipping {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover3));
  color: var(--text-color3);
}

.product-name {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-color1);
  line-height: 1.2;
  margin: 0;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  color: var(--text-color2);
  font-size: 14px;
}

.product-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.product-meta i {
  font-size: 12px;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.stars {
  display: flex;
  gap: 2px;
}

.stars .fa-star {
  font-size: 16px;
}

.stars .fa-star.filled {
  color: var(--star-filled);
}

.stars .fa-star-half-alt {
  background: var(--star-half);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stars .fa-regular {
  color: var(--star-empty);
}

.rating-text {
  color: var(--text-color2);
  font-weight: 500;
}

.review-link {
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 500;
  transition: opacity var(--transition-fast);
}

.review-link:hover {
  opacity: 0.8;
}

/* Price Section */
.price-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-lg) 0;
  border-top: 1px solid var(--border-color-hover1);
  border-bottom: 1px solid var(--border-color-hover1);
}

.price-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.price-current {
  font-size: 36px;
  font-weight: 800;
  color: var(--border-color-hover);
}

.price-original {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.original-price {
  font-size: 20px;
  color: var(--text-color2);
  text-decoration: line-through;
  font-weight: 500;
}

.savings-percent {
  background: rgba(255, 68, 68, 0.1);
  color: var(--status-error);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.price-saving {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--border-color-hover);
  font-weight: 600;
  font-size: 14px;
}

.tax-info {
  color: var(--text-color2);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: var(--spacing-xs);
}

/* Stock Section */
.stock-section {
  background: var(--background-color2);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  border: 1px solid var(--border-color-hover1);
}

.stock-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.stock-icon {
  font-size: 20px;
}

.stock-status:not(.out-of-stock) .stock-icon {
  color: var(--status-success);
}

.stock-status.out-of-stock .stock-icon {
  color: var(--status-error);
}

.stock-details {
  flex: 1;
}

.stock-text {
  font-weight: 600;
  color: var(--text-color1);
  margin-bottom: 2px;
}

.stock-quantity {
  font-size: 14px;
  color: var(--text-color2);
}

/* Description */
.description-section {
  padding: var(--spacing-md) 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color1);
  margin-bottom: var(--spacing-sm);
}

.product-description {
  line-height: 1.6;
  color: var(--text-color2);
  margin: 0;
  font-size: 15px;
}

.description-section--full {
  border-top: 1px solid var(--border-color-hover1);
  padding-top: var(--spacing-lg);
}

.product-description--full {
  white-space: pre-wrap;
}

/* Action Section */
.action-section {
  padding: var(--spacing-lg) 0;
}

.quantity-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
}

.quantity-label {
  font-weight: 600;
  color: var(--text-color1);
  min-width: 80px;
}

.quantity-control {
  display: flex;
  align-items: center;
  border: 2px solid var(--border-color-hover1);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--background-color2);
}

.qty-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.qty-btn:hover:not(:disabled) {
  background: var(--background-color3);
  color: var(--border-color-hover);
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-input {
  width: 60px;
  height: 44px;
  border: none;
  text-align: center;
  font-weight: 600;
  font-size: 16px;
  color: var(--text-color1);
  background: transparent;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.btn {
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 16px;
  border: none;
  outline: none;
  text-align: center;
  min-height: 48px;
}

.btn-primary {
  background: var(--border-color-hover);
  color: var(--button-color);
}

.btn-primary:hover:not(:disabled) {
  background: var(--border-color-hover3);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

/* Add to Cart: hover keeps color, only transformation */
.btn-add-cart.btn-primary:hover:not(:disabled) {
  background: var(--border-color-hover);
  color: var(--button-color);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.btn-secondary {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--border-color-hover1);
}

.btn-secondary:hover {
  background: var(--background-color2);
  border-color: var(--border-color-hover);
}

.btn-outline {
  background: transparent;
  border: 2px solid var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-outline:hover {
  background: var(--role-item);
  border-color: var(--border-color-hover3);
  color: var(--border-color-hover3);
}

/* Login to Review button: only transform on hover, no color change */
.btn-write-review.btn-outline {
  background: transparent;
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-write-review.btn-outline:hover {
  background: transparent !important;
  border-color: var(--border-color-hover) !important;
  color: var(--border-color-hover) !important;
  transform: translateY(-2px);
}

.btn-wishlist {
  background: transparent;
  border: 2px solid var(--border-color-hover1);
  color: var(--text-color1);
}

.btn-wishlist:hover,
.btn-wishlist.active {
  background: var(--role-item);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.cart-added {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.added-message {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--border-color-hover);
  font-weight: 600;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--role-item);
  border-radius: var(--radius-md);
}

.cart-actions {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .cart-actions {
    flex-direction: column;
  }
  
  .cart-actions .btn {
    width: 100%;
  }
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  padding: var(--spacing-lg) 0;
}

.feature-card {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--background-color2);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color-hover1);
  transition: all var(--transition-fast);
}

.feature-card:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

.feature-icon {
  font-size: 20px;
  color: var(--border-color-hover);
}

.feature-content h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color1);
}

.feature-content p {
  margin: 0;
  font-size: 13px;
  color: var(--text-color2);
  line-height: 1.4;
}

@media (max-width: 640px) {
  .features-grid {
    grid-template-columns: 1fr;
  }
}

/* Payment Section */
.payment-section {
  padding: 2px 0 0;
}

.payment-label {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-color2);
}

.payment-methods {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.payment-method {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 56px;
  padding: 6px 8px;
  background: transparent;
  border: 1px solid var(--border-color-hover);
  border-radius: 8px;
  cursor: default;
  transition: transform var(--transition-fast);
}

.payment-method img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.payment-method::after {
  content: attr(data-label);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 6px);
  transform: translateX(-50%) translateY(4px);
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--text-color1);
  color: var(--background-color1);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--transition-fast), transform var(--transition-fast);
  z-index: 2;
}

.payment-method:hover {
  transform: translateY(-2px);
}

.payment-method:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ================================
   PRODUCT TABS
   ================================ */
.product-tabs {
  margin-top: var(--spacing-xl);
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border-color-hover1);
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid var(--border-color-hover1);
  background: var(--background-color1);
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs-header::-webkit-scrollbar {
  display: none;
}

.tab-button {
  position: relative;
  flex: 0 0 auto;
  padding: var(--spacing-md) var(--spacing-lg);
  background: transparent;
  border: none;
  font-weight: 600;
  color: var(--text-color2);
  cursor: pointer;
  transition:
    color 0.3s ease,
    background 0.3s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: 16px;
  white-space: nowrap;
}

.tab-button::after {
  content: "";
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--border-color-hover);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.tab-button__icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  transition:
    transform 0.3s ease,
    background 0.3s ease,
    color 0.3s ease;
}

.tab-button:hover {
  color: var(--text-color1);
  background: color-mix(in srgb, var(--border-color-hover) 6%, transparent);
}

.tab-button:hover::after {
  transform: scaleX(0.5);
}

.tab-button:hover .tab-button__icon {
  transform: translateY(-2px) scale(1.06);
}

.tab-button.active {
  color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 8%, transparent);
}

.tab-button.active::after {
  transform: scaleX(1);
}

.tab-button.active .tab-button__icon {
  color: var(--background-color2);
  background: var(--border-color-hover);
}

.tab-button:focus-visible {
  outline: 2px solid var(--border-color-hover);
  outline-offset: -2px;
}

.tabs-content {
  padding: var(--spacing-xl);
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .tabs-content {
    padding: var(--spacing-lg) var(--spacing-md);
  }
  
  .tab-button {
    padding: var(--spacing-md) var(--spacing-md);
    font-size: 14px;
  }
}

/* Specifications */
.specs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md);
}

.spec-item {
  --spec-accent: var(--border-color-hover);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: 14px var(--spacing-md);
  background: var(--background-color1);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color-hover1);
  overflow: hidden;
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease,
    background 0.3s ease;
}

.spec-item::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: var(--spec-accent);
  transform: scaleY(0);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.spec-item--brand {
  --spec-accent: #3b82f6;
}

.spec-item--category {
  --spec-accent: #a855f7;
}

.spec-item--stock-in {
  --spec-accent: var(--status-success, #22c55e);
}

.spec-item--stock-out {
  --spec-accent: var(--status-error, #ef4444);
}

.spec-item--condition {
  --spec-accent: #f59e0b;
}

.spec-item:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--spec-accent) 55%, transparent);
  background: color-mix(in srgb, var(--spec-accent) 5%, var(--background-color1));
  box-shadow: 0 8px 22px color-mix(in srgb, var(--spec-accent) 18%, transparent);
}

.spec-item:hover::before {
  transform: scaleY(1);
}

.spec-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: var(--text-color1);
}

.spec-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 14px;
  color: var(--spec-accent);
  background: color-mix(in srgb, var(--spec-accent) 14%, transparent);
  transition:
    transform 0.3s ease,
    background 0.3s ease,
    color 0.3s ease;
}

.spec-item:hover .spec-icon {
  transform: scale(1.08) rotate(-6deg);
  color: #fff;
  background: var(--spec-accent);
}

.spec-value {
  color: var(--text-color2);
  font-weight: 500;
  text-align: right;
  transition: color 0.3s ease;
}

.spec-item:hover .spec-value {
  color: var(--text-color1);
}

.spec-value.in-stock,
.spec-value.out-stock {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--spec-accent);
  background: color-mix(in srgb, var(--spec-accent) 14%, transparent);
}

.spec-item:hover .spec-value.in-stock,
.spec-item:hover .spec-value.out-stock {
  color: var(--spec-accent);
}

.spec-value.in-stock::before,
.spec-value.out-stock::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

@media (max-width: 480px) {
  .specs-grid {
    grid-template-columns: 1fr;
  }
}

/* Shipping */
.shipping-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.shipping-option {
  --ship-accent: var(--border-color-hover);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  background: var(--background-color1);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color-hover1);
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease,
    background 0.3s ease;
}

.shipping-option:nth-child(1) {
  --ship-accent: #22c55e;
}

.shipping-option:nth-child(2) {
  --ship-accent: #3b82f6;
}

.shipping-option:nth-child(3) {
  --ship-accent: #f59e0b;
}

.shipping-option:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--ship-accent) 55%, transparent);
  background: color-mix(in srgb, var(--ship-accent) 5%, var(--background-color1));
  box-shadow: 0 10px 26px color-mix(in srgb, var(--ship-accent) 16%, transparent);
}

.shipping-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 20px;
  color: var(--ship-accent);
  background: color-mix(in srgb, var(--ship-accent) 14%, transparent);
  transition:
    transform 0.3s ease,
    background 0.3s ease,
    color 0.3s ease;
}

.shipping-option:hover .shipping-icon {
  transform: scale(1.08) rotate(-6deg);
  color: #fff;
  background: var(--ship-accent);
}

.shipping-content h4 {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--text-color1);
  font-size: 18px;
  font-weight: 600;
}

.shipping-content p {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--text-color2);
  line-height: 1.5;
}

.shipping-content {
  flex: 1;
  min-width: 0;
}

.shipping-areas {
  list-style: none;
  padding: 0;
  margin: var(--spacing-md) 0 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
}

.shipping-areas li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--background-color1);
  color: var(--text-color1);
  border: 1px solid color-mix(in srgb, var(--ship-accent) 30%, transparent);
  border-left: 3px solid var(--ship-accent);
  border-radius: var(--radius-md, 10px);
  font-size: 13.5px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: default;
  transition:
    background 0.25s ease,
    color 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s ease;
}

.shipping-areas li i {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
  color: var(--ship-accent);
  background: color-mix(in srgb, var(--ship-accent) 14%, transparent);
  transition: background 0.25s ease, color 0.25s ease;
}

.shipping-areas li span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shipping-areas li:hover {
  color: var(--ship-accent);
  border-color: var(--ship-accent);
  background: color-mix(in srgb, var(--ship-accent) 8%, var(--background-color1));
  box-shadow: 0 6px 16px color-mix(in srgb, var(--ship-accent) 18%, transparent);
  transform: translateY(-2px);
}

.shipping-areas li:hover i {
  color: #fff;
  background: var(--ship-accent);
}

@media (max-width: 480px) {
  .shipping-areas {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ================================
   REVIEWS SECTION - Fixed Issues
   ================================ */
.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  padding: 0;
  background: transparent;
  flex-wrap: wrap;
}

.reviews-summary {
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
  flex: 1;
  min-width: 300px;
}

.average-rating {
  text-align: center;
  min-width: 120px;
  flex-shrink: 0;
}

.rating-number {
  font-size: 48px;
  font-weight: 800;
  color: var(--text-color1);
  line-height: 1;
  margin-bottom: var(--spacing-sm);
}

.stars-large {
  color: var(--star-filled);
  font-size: 20px;
  margin: var(--spacing-sm) 0;
  display: flex;
  gap: 2px;
  justify-content: center;
}

.stars-large .fa-star {
  font-size: 20px;
}

.stars-large .fa-star.filled {
  color: var(--star-filled);
}

.stars-large .fa-star-half-alt {
  background: var(--star-half);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stars-large .fa-regular {
  color: var(--star-empty);
}

.rating-count {
  color: var(--text-color2);
  font-size: 14px;
}

.rating-distribution {
  flex: 1;
  min-width: 200px;
  max-width: 100%;
}

.no-distribution {
  flex: 1;
  display: flex;
  align-items: center;
  color: var(--text-color2);
  font-style: italic;
  font-size: 14px;
  min-height: 120px;
}

.rating-bar {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  gap: var(--spacing-sm);
}

.rating-label {
  width: 80px;
  font-size: 14px;
  color: var(--text-color1);
  font-weight: 500;
}

.bar-container {
  flex: 1;
  height: 8px;
  background: var(--border-color-hover1);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--star-filled);
  transition: width var(--transition-base);
}

.review-actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  min-width: 200px;
}

/* Tablet and below (768px) */
@media (max-width: 768px) {
  .reviews-header {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: stretch;
  }
  
  .reviews-summary {
    flex-direction: column;
    gap: var(--spacing-lg);
    width: 100%;
    min-width: 0;
  }
  
  .average-rating {
    min-width: 0;
    width: 100%;
  }
  
  .rating-number {
    font-size: 40px;
  }
  
  .stars-large {
    font-size: 18px;
  }
  
  .stars-large .fa-star {
    font-size: 18px;
  }
  
  .rating-distribution {
    width: 100%;
    min-width: 0;
  }
  
  .rating-bar {
    flex-wrap: wrap;
    gap: var(--spacing-xs);
  }
  
  .rating-label {
    width: 70px;
    font-size: 13px;
  }
  
  .bar-container {
    flex: 1;
    min-width: 100px;
  }
  
  .review-actions {
    width: 100%;
    align-items: stretch;
    min-width: 0;
  }
  
  .review-actions .btn {
    width: 100%;
  }
  
  .btn-write-review {
    width: 100%;
  }
  
  .already-reviewed {
    width: 100%;
    text-align: center;
    padding: var(--spacing-md);
  }
}

/* Small tablets and large phones (640px) */
@media (max-width: 640px) {
  .reviews-header {
    gap: var(--spacing-md);
  }
  
  .reviews-summary {
    gap: var(--spacing-md);
  }
  
  .rating-number {
    font-size: 36px;
  }
  
  .stars-large {
    font-size: 16px;
  }
  
  .stars-large .fa-star {
    font-size: 16px;
  }
  
  .rating-count {
    font-size: 13px;
  }
  
  .rating-label {
    width: 60px;
    font-size: 12px;
  }
  
  .bar-container {
    min-width: 80px;
  }
}

/* Review Form */
.review-form-container {
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  border: 1px solid var(--border-color-hover1);
  box-shadow: var(--shadow-sm);
}

@media (max-width: 640px) {
  .review-form-container {
    padding: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
  }
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
}

.form-header h4 {
  margin: 0;
  color: var(--text-color1);
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--background-color3);
  color: var(--text-color1);
}

.form-group {
  margin-bottom: var(--spacing-lg);
}

.form-group label {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
  color: var(--text-color1);
  font-size: 14px;
}

.star-rating {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  flex-wrap: wrap;
}

.star-rating i {
  font-size: 28px;
  color: var(--star-empty);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.star-rating i:hover {
  transform: scale(1.1);
}

.star-rating .filled {
  color: var(--star-filled);
}

.star-rating .fa-regular:hover {
  color: var(--star-filled);
}

.rating-text {
  margin-left: var(--spacing-md);
  font-weight: 600;
  color: var(--text-color1);
  font-size: 16px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: var(--spacing-md) var(--spacing-lg);
  border: 1px solid var(--border-color-hover1);
  border-radius: var(--radius-md);
  font-size: 16px;
  color: var(--text-color1);
  background: var(--background-color2);
  outline: none;
  transition: border-color var(--transition-fast);
  font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 2px var(--role-item);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: var(--text-color2);
  margin-top: var(--spacing-xs);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--border-color-hover1);
}

@media (max-width: 480px) {
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .btn {
    width: 100%;
  }
}

/* Reviews List */
.reviews-list {
  margin-top: var(--spacing-xl);
}

@media (max-width: 480px) {
  .reviews-list {
    margin-top: var(--spacing-lg);
  }
}

.reviews-filter {
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--background-color1);
  border-radius: var(--radius-md);
}

.reviews-filter h5 {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--text-color1);
  font-weight: 600;
  font-size: 16px;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.filter-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1);
  border-radius: 20px;
  color: var(--text-color2);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-btn:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover1);
}

.filter-btn.active {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  color: var(--text-color3);
}

.filter-count {
  font-size: 12px;
  opacity: 0.9;
}

.no-reviews {
  text-align: center;
  padding: var(--spacing-xl) var(--spacing-lg);
  color: var(--text-color2);
  background: var(--background-color1);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color-hover1);
}

.no-reviews i {
  font-size: 48px;
  margin-bottom: var(--spacing-md);
  color: var(--border-color-hover1);
  opacity: 0.7;
}

.no-reviews p {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
}

/* Review Cards - Fixed Text Display */
.reviews-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.review-card {
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  border: 1px solid var(--border-color-hover1);
  transition: all var(--transition-fast);
}

.review-card:hover {
  border-color: var(--border-color-hover);
  box-shadow: var(--shadow-sm);
}

.review-header {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-round);
  background: var(--border-color-hover);
  color: var(--text-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
}

.reviewer-info {
  flex: 1;
  min-width: 0; /* Allow flex shrinking */
  max-width: 100%;
}

.reviewer-name {
  font-weight: 600;
  color: var(--text-color1);
  margin-bottom: var(--spacing-xs);
  font-size: 16px;
}

.review-date {
  font-size: 13px;
  color: var(--text-color2);
}

.review-rating {
  display: flex;
  gap: 2px;
  color: var(--star-empty);
  flex-shrink: 0;
}

.review-rating .fa-star.filled {
  color: var(--star-filled);
}

.review-rating .fa-star {
  font-size: 16px;
}

.review-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color1);
  margin: 0 0 var(--spacing-sm) 0;
  line-height: 1.3;
}

.review-content {
  color: var(--text-color2);
  line-height: 1.6;
  margin: 0 0 var(--spacing-md) 0;
  font-size: 15px;
  white-space: pre-wrap; /* Preserve line breaks */
  word-break: break-word; /* Break long words */
}

.review-footer {
  display: flex;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--background-color3);
}

.helpful-btn,
.report-btn {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: 14px;
  border: 1px solid var(--border-color-hover1);
  background: var(--background-color2);
  color: var(--text-color2);
  cursor: pointer;
  transition: all var(--transition-fast);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  gap: 6px;
}

.helpful-btn:hover:not(:disabled) {
  background: var(--role-item);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.helpful-btn:disabled,
.helpful-btn.voted {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--background-color3);
  border-color: var(--border-color-hover1);
  color: var(--text-color2);
}

.helpful-btn.voted {
  background: var(--role-item);
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

/* Mobile phones (480px) */
@media (max-width: 480px) {
  .reviews-header {
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
  }
  
  .reviews-summary {
    gap: var(--spacing-md);
  }
  
  .average-rating {
    padding: var(--spacing-sm);
  }
  
  .rating-number {
    font-size: 32px;
  }
  
  .stars-large {
    font-size: 14px;
    gap: 1px;
  }
  
  .stars-large .fa-star {
    font-size: 14px;
  }
  
  .rating-count {
    font-size: 12px;
  }
  
  .rating-distribution {
    padding: var(--spacing-sm) 0;
  }
  
  .rating-bar {
    margin-bottom: var(--spacing-xs);
  }
  
  .rating-label {
    width: 50px;
    font-size: 11px;
  }
  
  .bar-container {
    min-width: 60px;
    height: 6px;
  }
  
  .review-actions {
    gap: var(--spacing-xs);
  }
  
  .review-form-container {
    padding: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
  }
  
  .form-header h4 {
    font-size: 18px;
  }
  
  .star-rating i {
    font-size: 24px;
  }
  
  .rating-text {
    font-size: 14px;
    margin-left: var(--spacing-sm);
  }
  
  .form-input,
  .form-textarea {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 14px;
  }
  
  .reviews-filter {
    padding: var(--spacing-md);
  }
  
  .reviews-filter h5 {
    font-size: 14px;
    margin-bottom: var(--spacing-xs);
  }
  
  .filter-buttons {
    gap: var(--spacing-xs);
  }
  
  .filter-btn {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 12px;
  }
  
  .review-card {
    padding: var(--spacing-md);
    border-radius: var(--radius-md);
  }
  
  .review-header {
    gap: var(--spacing-sm);
    margin-bottom: var(--spacing-sm);
  }
  
  .reviewer-avatar {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
  
  .reviewer-info {
    min-width: 0;
  }
  
  .reviewer-name {
    font-size: 14px;
    margin-bottom: 2px;
  }
  
  .review-date {
    font-size: 11px;
  }
  
  .review-rating {
    gap: 1px;
  }
  
  .review-rating .fa-star {
    font-size: 14px;
  }
  
  .review-title {
    font-size: 16px;
    margin-bottom: var(--spacing-xs);
  }
  
  .review-content {
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: var(--spacing-sm);
  }
  
  .review-footer {
    flex-direction: column;
    gap: var(--spacing-xs);
    padding-top: var(--spacing-sm);
  }
  
  .helpful-btn,
  .report-btn {
    width: 100%;
    justify-content: center;
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: 13px;
  }
}

/* Very small phones (360px) */
@media (max-width: 360px) {
  .rating-number {
    font-size: 28px;
  }
  
  .stars-large {
    font-size: 12px;
  }
  
  .stars-large .fa-star {
    font-size: 12px;
  }
  
  .rating-label {
    width: 45px;
    font-size: 10px;
  }
  
  .bar-container {
    min-width: 50px;
    height: 5px;
  }
  
  .review-card {
    padding: var(--spacing-sm);
  }
  
  .reviewer-avatar {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  .reviewer-name {
    font-size: 13px;
  }
  
  .review-date {
    font-size: 10px;
  }
  
  .review-title {
    font-size: 15px;
  }
  
  .review-content {
    font-size: 13px;
  }
  
  .filter-btn {
    padding: 6px var(--spacing-xs);
    font-size: 11px;
  }
}

/* ================================
   RELATED PRODUCTS
   ================================ */
.related-products {
  margin-top: var(--spacing-xxl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid var(--border-color-hover1);
}

.related-products .section-title {
  margin-bottom: var(--spacing-lg);
}

.related-products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--spacing-lg);
}

.related-product-card {
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border-color-hover1);
  transition: all var(--transition-fast);
  display: flex;
  flex-direction: column;
}

.related-product-card:hover {
  border-color: var(--border-color-hover);
  box-shadow: var(--shadow-md);
}

.related-product-image-wrapper {
  position: relative;
  aspect-ratio: 1;
  cursor: pointer;
  overflow: hidden;
  background: var(--background-color1);
}

.related-product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--transition-base);
}

.related-product-card:hover .related-product-image {
  transform: scale(1.05);
}

.related-product-badge {
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  background: linear-gradient(135deg, var(--status-error), #ff6b6b);
  color: var(--text-color3);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  font-weight: 700;
}

.related-product-info {
  padding: var(--spacing-md);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.related-product-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color1);
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.related-product-brand {
  font-size: 12px;
  color: var(--text-color2);
  margin: 0;
}

.related-product-price {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
  margin-top: auto;
}

.related-original-price {
  font-size: 12px;
  color: var(--text-color2);
  text-decoration: line-through;
}

.related-current-price {
  font-size: 16px;
  font-weight: 700;
  color: var(--border-color-hover);
}

.btn-learn-more {
  margin-top: var(--spacing-sm);
  width: 100%;
  font-size: 13px;
}

/* Learn More: hover keeps color, only transformation */
.btn-learn-more:hover {
  background: transparent;
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .related-products-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: var(--spacing-md);
  }

  .related-product-name {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .related-products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-sm);
  }

  .related-product-info {
    padding: var(--spacing-sm);
  }
}

/* ================================
   LOADING STATE
   ================================ */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: var(--spacing-xl) var(--spacing-lg);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--border-color-hover1);
  border-top: 3px solid var(--border-color-hover);
  border-radius: var(--radius-round);
  animation: spin 0.8s linear infinite;
  margin-bottom: var(--spacing-lg);
}

.loading-container p {
  color: var(--text-color2);
  font-size: 16px;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ================================
   ERROR STATE
   ================================ */
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: var(--spacing-xl) var(--spacing-lg);
}

.error-content {
  text-align: center;
  max-width: 400px;
  padding: var(--spacing-xl);
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

.error-content i {
  font-size: 64px;
  color: var(--border-color-hover1);
  margin-bottom: var(--spacing-lg);
  opacity: 0.7;
}

.error-content h2 {
  color: var(--text-color1);
  margin: 0 0 var(--spacing-md) 0;
  font-size: 24px;
  font-weight: 600;
}

.error-content p {
  color: var(--text-color2);
  margin: 0 0 var(--spacing-lg) 0;
  line-height: 1.5;
  font-size: 16px;
}

/* ================================
   MODAL
   ================================ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: var(--spacing-lg);
}

.modal-content {
  background: var(--background-color2);
  border-radius: var(--radius-lg);
  width: 100%;
  max-width: 400px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  animation: modalSlideIn var(--transition-base);
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg) var(--spacing-xl);
  border-bottom: 1px solid var(--border-color-hover1);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-color1);
  font-size: 20px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: var(--spacing-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: var(--background-color3);
  color: var(--text-color1);
}

.modal-body {
  padding: var(--spacing-xl);
  text-align: center;
}

.modal-body p {
  margin: 0 0 var(--spacing-lg) 0;
  color: var(--text-color2);
  font-size: 16px;
  line-height: 1.5;
}

.modal-body .btn {
  width: 100%;
}

/* ================================
   IMAGE TRANSITIONS
   Overlapping opacity swap — no slide, snapshot-like cut.
   ================================ */
.snapshot-enter-active,
.snapshot-leave-active {
  transition: opacity 120ms linear;
  position: absolute;
  inset: 0;
}

.snapshot-leave-active {
  z-index: 1;
}

.snapshot-enter-active {
  z-index: 2;
}

.snapshot-enter-from,
.snapshot-leave-to {
  opacity: 0;
}

/* ================================
   Z-INDEX DEFINITIONS
   ================================ */
.product-view {
  --z-dropdown: 1000;
  --z-sticky: 1020;
  --z-fixed: 1030;
  --z-modal-backdrop: 1040;
  --z-modal: 1050;
  --z-popover: 1060;
  --z-tooltip: 1070;
  --z-toast: 1080;
}

/* ================================
   RESPONSIVE TYPOGRAPHY
   ================================ */
@media (max-width: 990px) {
  .product-name {
    font-size: 28px;
  }
  
  .price-current {
    font-size: 32px;
  }
  
  .specs-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .product-view {
    padding-top: 70px; /* Extra padding for mobile */
  }
  
  .product-name {
    font-size: 24px;
  }
  
  .price-current {
    font-size: 28px;
  }
  
  .quantity-selector {
    flex-direction: column;
    align-items: stretch;
  }
  
  .quantity-label {
    margin-bottom: var(--spacing-sm);
  }
  
  .quantity-control {
    width: 100%;
  }
  
  .tab-button {
    padding: var(--spacing-md) var(--spacing-md);
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .product-view {
    padding-top: 80px; /* Even more padding for very small screens */
  }
  
  .product-name {
    font-size: 20px;
  }
  
  .price-current {
    font-size: 24px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .tab-button {
    padding: var(--spacing-md) var(--spacing-sm);
    font-size: 13px;
  }
  
  .review-form-container {
    padding: var(--spacing-lg);
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .btn {
    width: 100%;
  }
}
</style>